import { useEffect, useState } from 'react';
import { isFinalQuery, useReportingQuery, type ReportQuery } from '@eis/core/reporting';

/**
 * The viewer's data access — the one data layer (T-7), re-exported from `@eis/core`, which owns the
 * generated dataservice client.
 */
export {
  isPrivateReport,
  reportingReasonOf,
  useDataset,
  useCustomDataset,
  useDatasetFieldValues,
  useDuplicateReport,
  useExportReport,
  useQueryRowsLoader,
  useReport,
  useRunReport,
  useCancelQuery,
  useWorkspaceSettings,
  type Report,
  type ReportQuery,
} from '@eis/core/reporting';

export type RunPhase = 'IDLE' | 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELLED';

export interface QueryPolling {
  readonly query: ReportQuery | undefined;
  readonly phase: RunPhase;
  /** Seconds since the query was started — "Läuft seit n s". */
  readonly elapsedSeconds: number;
  readonly error: unknown;
}

/**
 * Polls one query until it is final (SDD-024 Q-1): every second, three after thirty seconds; the
 * clock ticks once a second while it runs so the run state can say for how long. A new id starts
 * the clock again.
 */
export function useQueryPolling(queryId: string | undefined): QueryPolling {
  // The clock restarts with a new id — adjusted during render, React's pattern for state that
  // follows a prop, rather than in an effect.
  const [clock, setClock] = useState(() => ({
    id: queryId,
    startedAt: Date.now(),
    now: Date.now(),
  }));
  if (clock.id !== queryId) {
    const at = Date.now();
    setClock({ id: queryId, startedAt: at, now: at });
  }
  const startedAt = clock.startedAt;
  const now = clock.now;
  const result = useReportingQuery(queryId, startedAt);
  const query = result.data;
  const final = isFinalQuery(query);
  useEffect(() => {
    if (queryId === undefined || final) return undefined;
    const timer = setInterval(() => {
      setClock((prev) => ({ ...prev, now: Date.now() }));
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [queryId, final]);
  const phase: RunPhase =
    queryId === undefined ? 'IDLE' : ((query?.state as RunPhase | undefined) ?? 'QUEUED');
  return {
    query,
    phase,
    elapsedSeconds: Math.max(0, Math.floor((now - startedAt) / 1000)),
    error: result.error,
  };
}
